/**
 * Story beats that fire when a department crosses a line.
 *
 * The one thing in the game the player cannot walk away from. Everything else
 * — an event, a favour, a shortfall — is a choice with a price; a chapter is a
 * door. It arms when standing crosses a threshold and it blocks the end of the
 * day until it has been played.
 *
 * The beats are written now. The shape they are written to is the one the gate
 * always had: a condition, a title, and some lines, which is why writing them
 * was an edit to this file and to nothing else.
 *
 * Four of the five are a department telling you where you now stand with it.
 * The fifth is not, and it is first for that reason. See the note on it.
 *
 * `when` is one department and one threshold. Deliberately not a boolean
 * expression over several — a beat that fires on a conjunction of four numbers
 * is a beat nobody can predict, and this has to feel like a consequence rather
 * than an ambush.
 */

export const CHAPTERS = [
  /**
   * The only beat in the file that is not a department drawing a line.
   *
   * Banquets is the section you are *in*, so the first thing that department
   * ever says to you should not be a threshold, it should be somebody sitting
   * down next to you. It fires at twenty rather than at the Captain's
   * forty-five and it is first in the array on purpose: `update()` arms the
   * first unplayed beat whose line has been crossed, so the crew take you in
   * before management puts your name on anything. Which is the right order and
   * is most of what the beat is saying.
   *
   * For a new starter this is also where the pen comes from, and it is Max who
   * hands it over, in the break room, between calls. Their pocket is empty
   * until he gives it to them, and the line is only crossed by grading
   * a room — so the first time anybody has it, they have already worked a
   * whole shift without it and know what that feels like. `gives` is what goes
   * in the pocket when the beat is played; nothing else in the game puts it
   * there.
   *
   * **Thirty-five, and it is thirty-five because Banquets starts at thirty.**
   * Your own supervisor starts warm — he hired you — so a threshold of twenty
   * is already crossed on the walk in, and the beat armed before the player had
   * touched anything: the pen arrived in the first minute of the first shift,
   * which is the one thing the paragraph above says it must not do. A graded
   * room pays this department twenty-two at an A and seven and a bit at a C, so
   * thirty-five is the first number that only a *finished room* can reach. The
   * rule is "one shift, then the pen", and this is what that number is now.
   */
  {
    id: 'ch-banquets-pen',
    when: { department: 'banquets', above: 35 },
    gives: ['pen'],
    title: 'Break Room, Twenty Past Four',
    speaker: 'Max "Milly Vanilly"',
    lines: [
      'One sec. Okay. Sit down, you have been on your feet since two.',
      'Here. Do not make a face, just take it.',
      'Everyone in this section has one. Nobody talks about it. That is the whole system.',
      'You will want it about eight, when the gala runs long and your hands start going.',
      'And you did not get it off me.',
    ],
  },
  {
    id: 'ch-kitchen-war',
    when: { department: 'kitchen', below: -35 },
    title: 'The Kitchen Has Opinions',
    speaker: 'Chef de Cuisine',
    lines: [
      'You have been setting rooms out of my stock for a week.',
      'I have said nothing. Do you know why I have said nothing?',
      'Because the day I say something, it stops being between us.',
      'So from tonight you sign for it or you go without. Ask Danny what I told him.',
    ],
  },
  /**
   * The supervisor deciding he trusts you, at the step of the ladder that
   * word already names.
   *
   * **Fifty-five, which is the floor of `trusted` in `standing.js`.** Not a
   * number picked to sit above the other two: it is the one line in the game
   * that already means what this beat says, so the beat fires exactly when
   * Banquets reaches the rung called trusted and not a room earlier. Any test
   * that wants to know why it is 55 should ask `floorOf('trusted')`.
   *
   * It was 45 while Banquets started at nothing, where that was two good
   * rooms. From a warm thirty it was one — a single A pays this department
   * twenty-two, which lands on 52 — and a beat that opens with *"four rooms
   * this week and not one of them came back at me"* after one room is a beat
   * calling the player a liar. At 55 it takes two rooms at their best and
   * four at a C, which is what the line says it takes.
   */
  {
    id: 'ch-banquets-trust',
    when: { department: 'banquets', above: 55 },
    title: 'Somebody Noticed',
    /*
     * The supervisor, not the Captain. He is the one on the roster who carries
     * `gates: 'assignment'`, which is the thing this beat is about: putting
     * your name on rooms is a decision only he can make, and the Captain sets
     * them rather than hands them out.
     */
    speaker: 'Banquet Supervisor',
    lines: [
      'Four rooms this week and not one of them came back at me.',
      'That does not happen. Not with new people.',
      'So I am going to start putting your name on things. You should know that.',
      'Starting with the gala. Do not thank me. Just do not drop it.',
    ],
  },
  {
    id: 'ch-management-mark',
    when: { department: 'management', below: -30 },
    title: 'A Word in the Office',
    speaker: 'Banquet Manager',
    lines: [
      'Three write-ups is the number. You know the number.',
      'I am not going to tell you what I think you have been doing.',
      'I am going to tell you that I have started writing it down.',
      'Dates, times, what you had in your hands. It is a short list and it is getting longer.',
    ],
  },
  {
    id: 'ch-housekeeping-debt',
    when: { department: 'housekeeping', below: -40 },
    title: 'The Linen Room Is Closed',
    speaker: 'Housekeeping Lead',
    lines: [
      'You have had four things off my carts and given back nothing.',
      'I am not angry. I am making a list.',
      'Ask me again this week and find out what is on it.',
      'Until it is settled the linen room is shut to you. Go and see how the Chef feels about you.',
    ],
  },
  /**
   * The joint, and a second person to owe something to.
   *
   * Alex, because of when and where he is. He is on the night strike, on from
   * nine, and the one person in the section still about when a PM shift lets
   * out at ten: out in the staff lot, on a smoke break, waiting to see if his
   * car will start. And because of what his shape is: Lil D covers and never
   * brings it up; Alex tells the section. The man who hands you the joint is
   * the man who talks, and he says so, which makes this a second relationship
   * with its own terms rather than a reward for the first one.
   *
   * It happens outdoors, in the staff lot, after a shift, `where` and `after`,
   * because the joint is what you do when nothing is due, and the lot at twenty
   * past ten is the one place on the property where nothing is. A beat with a
   * place arms only when you are standing in it with the shift behind you, and
   * plays there; it never sits in the one pending slot waiting for you to
   * wander outside. A new starter who never goes back out to the lot never
   * meets him, and never gets the joint. That is what it is for.
   *
   * Forty-two, above the pen's thirty-five: later in the week than the pen, by
   * a room or two, never on the first night. Both numbers moved together when
   * Banquets started warm; the gap between them is the rule, not the values,
   * and one graded room is what the gap is measured in.
   */
  {
    id: 'ch-alex-joint',
    when: { department: 'banquets', above: 42 },
    where: 'parking',
    after: 'shift',
    title: 'The Staff Lot, Twenty Past Ten',
    speaker: 'Alex',
    lines: [
      'Nobody goes straight home off a banquet. Sit on the kerb, mind the glass. My car will not start anyway.',
      'Here. Not the pen. The pen is for when you are working. This is for when you are not.',
      'Out here, and only out here. Never inside, never on the clock, and never where the valets can see you.',
      'And I talk. The whole section knows I talk. If you ever want something kept, do not bring it to me.',
      'Go on. I have got a ballroom to strike, if I ever finish this one.',
    ],
    gives: ['joint'],
  },
  /**
   * Danny, at the pass, wanting Friday's lawn dinner over.
   *
   * The Chef at the pass rather than anybody in the section, because the
   * dinner is his problem and nobody else's. Eight covers on a lawn is every
   * plate walked three hundred feet across the staff lot and back, on the
   * double, on the busiest night he has, and it runs until ten: his line goes
   * home at midnight for a table of eight. He does not want it cancelled — he
   * cannot, and he knows that — he wants it to end early, and he wants it to
   * be nobody's fault. Which is what the desert is for.
   *
   * It is his to ask because of what he is: a gate, not a colleague, so it is
   * Kitchen's opinion of you that decides whether he trusts you with it, and
   * the line is `warm` — `floorOf('warm')` in `standing.js`, the same step that
   * starts leaving his stock nearer the room. And because of his shape: he
   * tells everyone. The man who asks you to do this is the last man you would
   * trust to keep it, and he says so.
   *
   * At the pass, where he is, and it plays there. `posts` is the dinner it
   * asks about; ending that dinner early, on any night after this, is what he
   * asked for, and what it is worth is on the dinner's row.
   */
  {
    id: 'ch-chef-dinner',
    when: { department: 'kitchen', above: 25 },
    where: 'kitchen',
    posts: 'breeze-dinner',
    title: 'The Pass, Before Service',
    speaker: 'Danny Obando',
    lines: [
      'Friday. The lawn dinner. Eight covers, three hundred feet from my pass, until ten.',
      'I cannot cancel it. Nobody can cancel it. I would just like it to be over by eight.',
      'You know what comes up out of that desert after dark. You know what it eats.',
      'The break room has oranges. The staff lot has a golf cart. I have said nothing.',
      'And do not let anybody see you. I tell everybody everything, and you know that too.',
    ],
  },
  /**
   * Hollis, in banquet storage, needing the air walls run.
   *
   * Hollis because the brunch is hers: she is the AM lead, it is her room going
   * up, and the brunch wants the Crown Ballroom in three. Running the air
   * walls out is one man's job on the AM crew and he is never at his post; she
   * knows where storage is and where the ballroom is and she does not have time
   * to walk between them looking. It is an errand, which is the right size for
   * somebody who has just set their first room, so it asks for the same step of
   * Banquets the pen does.
   *
   * In his hours only (`hours`): he is in every morning and off at four.
   * `posts` is the search it starts; see `data/searches.js`.
   */
  {
    id: 'ch-hollis-walls',
    when: { department: 'banquets', above: 35 },
    where: 'storage',
    hours: ['06:30', '13:30'],
    posts: 'find-juanito',
    // Not once he has walked out: there is nobody left to run the walls.
    unless: 'reported',
    title: 'Banquet Storage, Before Brunch',
    speaker: 'Hollis Trask',
    lines: [
      'Brunch wants the ballroom in three and nobody has run the walls out.',
      'Juanito runs the walls. Juanito is not at his post. Juanito is never at his post.',
      'Find him and send him to me. Ask around. Somebody always knows.',
      'He is off at four, so it is this morning or it is not at all.',
    ],
  },
  /**
   * The Valet Captain, at the lot, with a guest out past the courts and a lion
   * between them and the hotel.
   *
   * Him because the golf cart is his: it is Valet's cart, for running guests
   * out to the courts and back, and the guest who walked back from the courts
   * this evening is one of his. He cannot leave the gate, and he knows what is
   * out there after dark, and he has seen you drive. So it is Valet's opinion
   * of you that decides whether he hands you the cart for it, and the line is
   * a little goodwill rather than none.
   *
   * In the lot, where the cart is, in the evening, when the lions are about
   * (`hours`). `posts` is the rescue; see `data/rescues.js`.
   */
  {
    id: 'ch-valet-lion',
    when: { department: 'valet', above: 10 },
    where: 'parking',
    hours: ['19:00', '21:30'],
    posts: 'lion-rescue',
    title: 'The Staff Lot, After Dark',
    speaker: 'Valet Captain',
    lines: [
      'The guest who walked out to the courts has not come back. And there is a lion by the ninth. I have seen it.',
      'I cannot leave the gate. You can drive the cart. Keys are in it.',
      'It hears that cart. Everything out there hears that cart. Go in quiet.',
      'Get them in the back and get them to the lot. Do not try to be clever with it.',
    ],
  },
  /**
   * The lobby clock, and the end of the third act.
   *
   * It stops at four on a day the man from the ownership group has a meeting,
   * once Management is warm (`sim/story.js`). Priya tells you, at her desk,
   * because the clock is over it. Nobody knows why it stopped and nobody says
   * anything about four o'clock, because nobody has any reason to.
   *
   * `needs` is the story's flag. Management above the warm line again is the
   * `when`, which the flag already implies, because every beat has one.
   */
  {
    id: 'ch-lobby-clock',
    when: { department: 'management', above: 25 },
    needs: 'clock-stopped',
    where: 'lobby',
    title: 'The Lobby, After Four',
    speaker: 'Priya Raghunathan',
    lines: [
      'The clock over my desk stopped at four. Nobody touched it. I was standing right here.',
      'Engineering says Thursday. Engineering always says Thursday.',
      'Guests keep asking me the time. I keep telling them four.',
      'Anyway. The man with the tablet wants the section in the alcove. All of you.',
    ],
  },
  /**
   * The plan, said out loud.
   *
   * In the alcove, at the board, which is where the section reads what is
   * coming. He is polite about it, he calls you team, and he does not know your
   * name. `posts: 'finale'` puts his dinner on tomorrow's board.
   */
  {
    id: 'ch-grant-plan',
    when: { department: 'management', above: 25 },
    needs: 'clock-stopped',
    follows: 'ch-lobby-clock',
    where: 'alcove',
    posts: 'finale',
    title: 'The Muster Alcove, A Quick One',
    speaker: 'Grant Whitfield',
    lines: [
      'Team. Quick one, I know you are busy. Love the energy.',
      'The group has asked me to look at an outside staffing agency for banquets. Cheaper, flexible, no overtime.',
      'I have not decided anything. Tomorrow there is a dinner for the group, and I will be in the room.',
      'Show me the building is behind you. That is all I am asking, team.',
    ],
  },

  /*
   * --- the road -------------------------------------------------------------
   *
   * Four jobs the cart road makes possible, each asked by the one person on the
   * roster it is actually the problem of, where they are, on the day it would
   * happen. Each `posts` one row; what it is worth is on the row.
   */

  /**
   * The ice run: Imani Blake, the steward.
   *
   * Ice is a steward's to count out, and she is the one who counts: "I count it
   * in and I count it out." A satellite outlet short of ice is her problem the
   * moment somebody rings the kitchen, and the ice cart lives by the kitchen
   * door, so that is where she finds you. Saturday, when the Caytons has its
   * weddings. Banquets has to trust you with a count first: the trusted line.
   */
  {
    id: 'ch-steward-ice',
    when: { department: 'banquets', above: 55 },
    where: 'kitchen',
    days: [5],
    hours: ['15:00', '18:00'],
    posts: 'ice-run',
    title: 'The Kitchen Door, Mid Afternoon',
    speaker: 'Imani Blake',
    lines: [
      'The Caytons have a wedding on the terrace and no ice. Their machine died at noon.',
      'Twelve bags on the cart by the door. Down the stairs to the apron, onto the golf cart, down the road.',
      'It is over a hundred out there. It will not wait for you.',
      'Slow on the bumps and slow on the bends. Twelve out. I want to hear twelve arrived.',
    ],
  },
  /**
   * The pool set: Priya Raghunathan, the Front Desk.
   *
   * The pool is hers: the guests in it are hers, the complaints from it are
   * hers, and a function out there that the pool stays open for is a thing
   * she has to live with rather than a thing she booked. Wednesday afternoon,
   * at her desk, once the morning shift is off the clock, about Thursday
   * evening, which is a late shift and the pool's last hour. Front Desk warm,
   * because she would not ask a stranger.
   */
  {
    id: 'ch-desk-pool',
    when: { department: 'frontdesk', above: 25 },
    where: 'lobby',
    days: [2],
    hours: ['14:00', '18:00'],
    posts: 'pool-set',
    title: 'The Front Desk, About Tomorrow',
    speaker: 'Priya Raghunathan',
    lines: [
      'Tomorrow evening, forty-eight covers on the pool deck. Sales sold it and told me after.',
      'The pool stays open. I cannot close a pool in the afternoon with guests in it.',
      'So they will be in the water watching you set it. Every table, every trip.',
      'Nothing goes in the pool and nobody runs on the deck. Make me look good.',
    ],
  },
  /**
   * The burros: Tino Marchetti, the Valet gate.
   *
   * The shuttle is Valet's. His drivers take it down the cart road to the
   * gatehouse, and he stands at the dock doors all day looking out the way
   * the vehicles come in, so he is the one who hears the road is blocked.
   * Sunday afternoon. Valet has to think well enough of you to hand you the
   * job rather than wait it out.
   */
  {
    id: 'ch-valet-burros',
    when: { department: 'valet', above: 20 },
    where: 'dock',
    days: [6],
    hours: ['12:00', '16:00'],
    posts: 'burros-on-the-road',
    title: 'The Dock Doors, Sunday',
    speaker: 'Tino Marchetti',
    lines: [
      'Burros. On the cart road, down by the wash, standing there like they paid for it.',
      'The shuttle goes down to the gate inside the hour and my driver will not go through them.',
      'Do not hit them and do not throw anything at them. The county loves them more than it loves us.',
      'They like apples. Everybody knows they like apples.',
    ],
  },
  /**
   * The shuttle: the Valet Captain.
   *
   * The golf cart is his, the guests are his, and the van that would have
   * taken them is not here. He cannot leave the gate, which is the same reason
   * he asks you out to the lion. Saturday evening, in the lot, with an hour to
   * do a half-hour drive in.
   */
  {
    id: 'ch-valet-shuttle',
    when: { department: 'valet', above: 25 },
    where: 'parking',
    days: [5],
    hours: ['18:30', '19:00'],
    posts: 'caytons-shuttle',
    title: 'The Staff Lot, Saturday Evening',
    speaker: 'Valet Captain',
    lines: [
      'Two guests for dinner at the Caytons at half seven, and the van is at the airport.',
      'So it is the golf cart, and it is you. They are waiting by it.',
      'Mind the branches on the bend and whatever is lying in the road. They are paying for dinner, not for that.',
      'Half seven. Not twenty to eight.',
    ],
  },
];

export default CHAPTERS;
